/** A single parsed Server-Sent Event: its `event:` name and joined `data:` payload. */
export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Extracts complete SSE frames from a buffer of streamed text.
 *
 * Frames are separated by a blank line (`\n\n`). Any text after the final
 * separator is an incomplete frame — it is returned as `rest` so the caller can
 * prepend it to the next chunk. Multiple `data:` lines in one frame are joined
 * with `\n` per the SSE spec; a frame with no `event:` line defaults to
 * `'message'`.
 */
export function parseSseFrames(buffer: string): { events: SseEvent[]; rest: string } {
  const blocks = buffer.split('\n\n');
  // The last element is whatever came after the final `\n\n` — possibly a
  // partial frame still being streamed. Hold it back for the next read.
  const rest = blocks.pop() ?? '';

  const events: SseEvent[] = [];
  for (const block of blocks) {
    if (!block.trim()) continue;
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trimStart());
    }
    events.push({ event, data: dataLines.join('\n') });
  }
  return { events, rest };
}
